import type { OutlineSection } from './prototype2Data';

const OUTLINE_BLOCK_RE = /<<<OUTLINE_BEGIN>>>([\s\S]*?)<<<OUTLINE_END>>>/;

const isValidOutlineSections = (value: unknown): value is OutlineSection[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as OutlineSection).title === 'string' &&
      Array.isArray((entry as OutlineSection).bullets) &&
      (entry as OutlineSection).bullets.every((bullet) => typeof bullet === 'string')
  );

const parseOutlineFromText = (text: string): OutlineSection[] => {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const sections: OutlineSection[] = [];
  let current: OutlineSection | null = null;

  for (const line of lines) {
    const numbered = line.match(/^(?:#{1,3}\s*)?(\d+)\.\s+(.+)$/);
    if (numbered) {
      if (current) sections.push(current);
      current = { title: `${numbered[1]}. ${numbered[2]}`, bullets: [] };
      continue;
    }

    const bullet = line.match(/^(?:[-*•]|\u2022)\s+(.+)$/);
    if (bullet && current) {
      current.bullets.push(bullet[1]);
    }
  }

  if (current) sections.push(current);
  return sections;
};

interface ParsedOutlineResponse {
  chat: string;
  sections?: OutlineSection[];
}

const stripMarkers = (text: string): string =>
  text
    .replace(/<<<OUTLINE_BEGIN>>>[\s\S]*?<<<OUTLINE_END>>>/g, '')
    .replace(/<<<[A-Z_]+>>>/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const parseOutlineResponse = (raw: string): ParsedOutlineResponse => {
  const text = raw ?? '';
  const blockMatch = text.match(OUTLINE_BLOCK_RE);

  if (blockMatch) {
    try {
      const sections = JSON.parse(blockMatch[1].trim()) as unknown;
      if (isValidOutlineSections(sections)) {
        const chat = stripMarkers(text);
        return {
          chat: chat || "I've updated the outline based on your feedback.",
          sections,
        };
      }
    } catch {
      /* fall through to text parser */
    }
  }

  const sections = parseOutlineFromText(text);
  if (sections.length > 0) {
    return {
      chat: "I've updated the outline based on your feedback.",
      sections,
    };
  }

  return { chat: text };
};

export { parseOutlineResponse };
